import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { useCallback } from 'react'
import * as React from 'react'

import TiptapEditor from './TiptapEditor'
import { useUsers as useUsersContext } from '../../contexts/UserContext'
import { LOCATIONS, SPORTS } from '../../share/Constant'
import { AvatarPlus, UserAvatar } from '../../utils/gravatar'
import { getDayjsFromTime, timeToHHMMSS } from '../../utils/TimeUtility'
import { getSportProps } from '../../utils/utility'
import PictureManagement from '../cards/PictureManagement'
import useBar from '../common/Bar'
import { useUsers } from '../hooks/useUsers'

const SPORTS_PROPS = Object.values(SPORTS).map((sport) => getSportProps(sport))

const FormSectionLabel = ({ children, required = false }) => (
  <FormLabel
    sx={{
      mb: 2,
      display: 'block',
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.15rem',
      color: 'text.primary',
      '& .MuiFormLabel-asterisk': {
        color: 'error.main',
      },
    }}
    required={required}
  >
    {children}
  </FormLabel>
)

// GroupSessionForm can be used for create and edit
export default function GroupSessionForm({ formData = {}, formErrors = {}, onChange }) {
  const bar = useBar()
  const { users, fetchAllUsers } = useUsersContext()
  const [selectedParticipants, setSelectedParticipants] = React.useState(formData?.participantIds || [])
  const [useAbsoluteDeadlines, setUseAbsoluteDeadlines] = React.useState(formData.useAbsolute || formData.registrationDeadlineHours != null)
  const [registrationDeadlineHours, setRegistrationDeadlineHours] = React.useState(formData.registrationDeadlineHours || 12)
  const [cancellationDeadlineHours, setCancellationDeadlineHours] = React.useState(formData.cancellationDeadlineHours || 24)
  const [addParticipantDialogOpen, setAddParticipantDialogOpen] = React.useState(false)
  const [availableUsers, setAvailableUsers] = React.useState([])
  const [searchQuery, setSearchQuery] = React.useState('')
  const [loadingAvailableUsers, setLoadingAvailableUsers] = React.useState(false)

  const [participantCandidateIds, setParticipantCandidateIds] = React.useState(formData.participantIds || [])
  const { users: participantUsers, isLoading: isLoadingParticipants } = useUsers(participantCandidateIds || [])

  React.useEffect(() => {
    setAvailableUsers(Object.values(users || {}))
  }, [users])

  const handleChange = useCallback(
    (field, value) => {
      if (field === 'date') {
        const sessionDate = dayjs(value)
        const date = sessionDate.format('YYYY-MM-DD')
        const registrationDeadline = sessionDate.subtract(registrationDeadlineHours, 'hours').toISOString()
        const cancellationDeadline = sessionDate.subtract(cancellationDeadlineHours, 'hours').toISOString()
        return onChange({ date, registrationDeadline, cancellationDeadline })
      }

      if (field === 'price') {
        value = Number(value)
        return onChange({ [field]: value })
      }

      if (field === 'participantIds') {
        if (value?.length <= formData.maxParticipants) {
          return onChange({ participantIds: value })
        }
        return
      }

      if (field === 'minParticipants') {
        value = Number(value)
        return onChange({ [field]: value })
      }

      if (field === 'maxParticipants') {
        value = Number(value)
        return onChange({ [field]: value })
      }

      // Handle direct deadline changes
      if (field === 'registrationDeadline' || field === 'cancellationDeadline') {
        return onChange({ [field]: value })
      }

      // Handle relative deadline changes (hours before)
      if (field === 'registrationDeadlineHours' || field === 'cancellationDeadlineHours') {
        // This is not working, and handled differently
        const sessionDate = dayjs(formData.date)
        if (!sessionDate.isValid()) {
          return onChange({ [field]: value })
        }

        let registrationDeadlineHoursLocal = registrationDeadlineHours
        let cancellationDeadlineHoursLocal = cancellationDeadlineHours

        if (field === 'registrationDeadlineHours') {
          setRegistrationDeadlineHours(value)
          registrationDeadlineHoursLocal = Number(value)
        }

        if (field === 'cancellationDeadlineHours') {
          setCancellationDeadlineHours(value)
          cancellationDeadlineHoursLocal = Number(value)
        }

        return onChange({
          registrationDeadlineHours: registrationDeadlineHoursLocal,
          cancellationDeadlineHours: cancellationDeadlineHoursLocal,
          registrationDeadline: sessionDate.subtract(registrationDeadlineHoursLocal, 'hours').toISOString(),
          cancellationDeadline: sessionDate.subtract(cancellationDeadlineHoursLocal, 'hours').toISOString(),
        })
      }

      // Handle all other field changes
      onChange({ [field]: value })
    },
    [registrationDeadlineHours, cancellationDeadlineHours, onChange, formData],
  )

  const detailsRef = useRef(formData.details || '')
  const handleDetailsChange = useCallback(
    (value) => {
      onChange({ details: value })
    },
    [onChange],
  )

  const handleParticipantToggle = React.useCallback(
    (participantId) => {
      setSelectedParticipants((prev) => {
        if (prev.includes(participantId)) {
          const newParticipants = prev.filter((id) => id !== participantId)
          handleChange('participantIds', newParticipants)
          return newParticipants
        } else {
          if (prev?.length >= formData.maxParticipants) {
            bar.warn('You have reached the maximum number of participants')
            return prev
          }

          const newParticipants = [...prev, participantId]
          handleChange('participantIds', newParticipants)
          return newParticipants
        }
      })
    },
    [formData.maxParticipants, handleChange, selectedParticipants],
  )

  const handleDeadlineTypeChange = (event) => {
    const useAbsolute = event.target.checked
    setUseAbsoluteDeadlines(useAbsolute)
    onChange({
      useAbsolute: useAbsolute,
      registrationDeadlineHours: Number(registrationDeadlineHours),
      cancellationDeadlineHours: Number(cancellationDeadlineHours),
    })
  }

  const handleAddTimeSlot = () => {
    const times = formData.times || []
    const newTimeSlot = {
      startTime: '10:00:00',
      endTime: '12:00:00',
    }

    if (times.length > 0) {
      const lastTimeSlot = times[times.length - 1]
      const lastEndTime = getDayjsFromTime(lastTimeSlot.endTime)
      newTimeSlot.startTime = timeToHHMMSS(lastEndTime)
      newTimeSlot.endTime = timeToHHMMSS(lastEndTime.add(2, 'hours'))
    }
    const updatedTimes = [...times, newTimeSlot]
    handleChange('times', updatedTimes)
  }

  const handleRemoveTimeSlot = (index) => {
    const updatedTimes = formData.times?.filter((_, i) => i !== index) || []
    handleChange('times', updatedTimes)
  }

  const handleTimeSlotChange = (index, field, value) => {
    const updatedTimes = [...(formData.times || [])]

    const timeslot = updatedTimes[index]
    if (field === 'startTime') {
      timeslot.startTime = value
      // Set end time to 1 hour after start time if start time is later than end time
      if (!timeslot.endTime || getDayjsFromTime(value).isAfter(getDayjsFromTime(timeslot.endTime))) {
        timeslot.endTime = timeToHHMMSS(getDayjsFromTime(value).add(1, 'hour'))
      }
    } else {
      timeslot.endTime = value
      // If end time is earlier than start time, set start time to 1 hour before end time
      if (timeslot.startTime && getDayjsFromTime(value).isBefore(getDayjsFromTime(timeslot.startTime))) {
        timeslot.startTime = timeToHHMMSS(getDayjsFromTime(value).subtract(1, 'hour'))
      }
    }

    updatedTimes[index] = timeslot
    handleChange('times', updatedTimes)
  }

  const handleAddParticipantClick = async () => {
    setAddParticipantDialogOpen(true)
    setLoadingAvailableUsers(true)
    try {
      const response = await fetchAllUsers()
      setAvailableUsers(Object.values(response || {}))
    } catch (err) {
      console.error('Failed to load available users:', err)
    } finally {
      setLoadingAvailableUsers(false)
    }
  }

  const handleAddParticipantClose = () => {
    setAddParticipantDialogOpen(false)
    setSearchQuery('')
  }

  const handleParticipantSelect = (userId) => {
    if (!participantCandidateIds?.includes(userId)) {
      setParticipantCandidateIds([...(participantCandidateIds || []), userId])

      if (formData.maxParticipants > selectedParticipants?.length) {
        setSelectedParticipants([...(selectedParticipants || []), userId])
        handleChange('participantIds', [...(formData.participantIds || []), userId])
      }
    }
    handleAddParticipantClose()
  }

  const filteredUsers = React.useMemo(() => {
    // TODO Show a button to show more
    return availableUsers
      .filter((user) => {
        const searchLower = searchQuery.toLowerCase()
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
        const email = user.email?.toLowerCase() || ''
        return fullName.includes(searchLower) || email.includes(searchLower)
      })
      .slice(0, 5)
  }, [availableUsers, searchQuery])

  return (
    // Add align of form section to center
    <Card sx={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
        {/* Title */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Session Title</FormSectionLabel>
          <Box px={2}>
            <TextField
              fullWidth
              value={formData.title || ''}
              onChange={(e) => handleChange('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
              placeholder="Enter session title"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
              }}
            />
          </Box>
        </FormControl>

        {/* Sport Type Selection */}
        <FormControl fullWidth error={!!formErrors.sport} sx={{ mb: 2 }}>
          <FormSectionLabel required>Sport Type</FormSectionLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
            {SPORTS_PROPS.map((type) => (
              <Button
                key={type.value}
                variant={formData.sport === type.value ? 'contained' : 'outlined'}
                onClick={() => handleChange('sport', formData.sport === type.value ? '' : type.value)}
                startIcon={type.icon}
                sx={{
                  borderRadius: 8,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  fontSize: '0.875rem',
                  borderWidth: 1,
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                  },
                }}
              >
                {type.label}
              </Button>
            ))}
          </Stack>
          {formErrors.sport && <FormHelperText>{formErrors.sport}</FormHelperText>}
        </FormControl>

        {/* Location Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Location</FormSectionLabel>
          <Card sx={{ p: 2, bgcolor: 'background.paper', border: 'none', boxShadow: 'none' }}>
            <FormControl fullWidth error={!!formErrors.skiResort}>
              <InputLabel>Select Resort</InputLabel>
              <Select value={formData.skiResort || ''} label="Select Resort" onChange={(e) => handleChange('skiResort', e.target.value)}>
                {LOCATIONS.map((location) => (
                  <MenuItem key={location.value} value={location.value}>
                    {location.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.skiResort && <FormHelperText>{formErrors.skiResort}</FormHelperText>}
            </FormControl>
          </Card>
        </FormControl>

        {/* Session Details Form */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Session Details</FormSectionLabel>
          {/* Price and Participant Limits */}
          <Grid p={2} container alignItems="center" justifyContent="center" spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
              <TextField
                fullWidth
                label="Basic Price"
                value={formData.price || ''}
                onChange={(e) => handleChange('price', e.target.value)}
                error={!!formErrors.price}
                helperText={formErrors.price}
                variant="outlined"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid container size={{ xs: 12, sm: 6, md: 8, lg: 8 }} spacing={2}>
              <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  label="Minimum Participants"
                  type="number"
                  value={formData.minParticipants || ''}
                  onChange={(e) => handleChange('minParticipants', e.target.value)}
                  error={!!formErrors.minParticipants}
                  helperText={formErrors.minParticipants}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  label="Maximum Participants"
                  type="number"
                  value={formData.maxParticipants || ''}
                  onChange={(e) => handleChange('maxParticipants', e.target.value)}
                  error={!!formErrors.maxParticipants}
                  helperText={formErrors.maxParticipants}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Session Time */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Session Time</FormSectionLabel>
          <Grid container alignItems="center" justifyContent="center" spacing={2} p={2}>
            {/* Session Date picker */}
            <Grid size={12}>
              <DatePicker
                label="Session Date"
                value={formData.date ? dayjs(formData.date) : null}
                onChange={(newValue) => handleChange('date', newValue?.toISOString())}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!formErrors.date,
                    helperText: formErrors.date,
                  },
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                  },
                }}
              />
            </Grid>

            {/* Time Slots */}
            <Grid size={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Time Slots
                </Typography>
                {formData.times?.map((timeSlot, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TimePicker
                      label="Start Time"
                      value={timeSlot.startTime ? getDayjsFromTime(timeSlot.startTime) : null}
                      onChange={(newValue) => handleTimeSlotChange(index, 'startTime', timeToHHMMSS(newValue))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      to
                    </Typography>
                    <TimePicker
                      label="End Time"
                      value={timeSlot.endTime ? getDayjsFromTime(timeSlot.endTime) : null}
                      onChange={(newValue) => handleTimeSlotChange(index, 'endTime', timeToHHMMSS(newValue))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                    <IconButton onClick={() => handleRemoveTimeSlot(index)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} onClick={handleAddTimeSlot} variant="outlined" size="small" sx={{ mt: 1 }}>
                  Add Time Slot
                </Button>
              </Box>
            </Grid>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Deadlines
            </Typography>
            {/* Absolute deadline pickers for registration and cancellation, disabled when relative deadlines are used */}
            <Grid container alignItems="center" justifyContent="center" size={12}>
              {/* Allows setting registration deadline when absolute deadlines are enabled */}
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                <DateTimePicker
                  label="Registration Deadline"
                  value={formData.registrationDeadline ? dayjs(formData.registrationDeadline) : null}
                  onChange={(newValue) => handleChange('registrationDeadline', newValue?.toISOString())}
                  readOnly={useAbsoluteDeadlines}
                  disabled={useAbsoluteDeadlines}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.registrationDeadline,
                      helperText: formErrors.registrationDeadline,
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                {/* Allows setting cancellation deadline when absolute deadlines are enabled */}
                <DateTimePicker
                  label="Cancellation Deadline"
                  value={formData.cancellationDeadline ? dayjs(formData.cancellationDeadline) : null}
                  onChange={(newValue) => handleChange('cancellationDeadline', newValue?.toISOString())}
                  readOnly={useAbsoluteDeadlines}
                  disabled={useAbsoluteDeadlines}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.cancellationDeadline,
                      helperText: formErrors.cancellationDeadline,
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Toggle switch to control deadline input type - absolute dates vs relative hours */}
            <Grid container alignItems="center" justifyContent="center" size={12}>
              <FormControlLabel
                control={<Switch checked={useAbsoluteDeadlines} onChange={handleDeadlineTypeChange} />}
                //   label={useAbsoluteDeadlines ? 'Use absolute deadlines' : 'Use relative deadlines (hours before)'}
                label={'Use relative deadlines (hours before)'}
              />
            </Grid>

            {/* Relative deadline inputs shown when useAbsoluteDeadlines is true */}
            <Grid container alignItems="center" justifyContent="center" size={12}>
              {useAbsoluteDeadlines && (
                <>
                  {/* Registration deadline in hours before session start */}
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <FormControl fullWidth sx={{ m: 1 }} error={!!formErrors.registrationDeadlineHours}>
                      <InputLabel htmlFor="registration-deadline-hours">Registration Deadline</InputLabel>
                      <OutlinedInput
                        id="registration-deadline-hours"
                        endAdornment={<InputAdornment position="start"> hours before</InputAdornment>}
                        type="number"
                        value={registrationDeadlineHours}
                        onChange={(e) => handleChange('registrationDeadlineHours', Number(e.target.value))}
                        error={!!formErrors.registrationDeadlineHours}
                        inputProps={{
                          style: {
                            textAlign: 'right',
                            paddingRight: '8px',
                          },
                        }}
                      />
                      {formErrors.registrationDeadlineHours && <FormHelperText>{formErrors.registrationDeadlineHours}</FormHelperText>}
                    </FormControl>
                  </Grid>

                  {/* Cancellation deadline in hours before session start */}
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <FormControl fullWidth sx={{ m: 1 }} error={!!formErrors.cancellationDeadlineHours}>
                      <InputLabel htmlFor="cancellation-deadline-hours">Cancellation Deadline</InputLabel>
                      <OutlinedInput
                        id="cancellation-deadline-hours"
                        endAdornment={<InputAdornment position="start"> hours before</InputAdornment>}
                        type="number"
                        value={cancellationDeadlineHours}
                        onChange={(e) => handleChange('cancellationDeadlineHours', Number(e.target.value))}
                        error={!!formErrors.cancellationDeadlineHours}
                        inputProps={{
                          style: {
                            textAlign: 'right',
                            paddingRight: '8px',
                          },
                        }}
                      />
                      {formErrors.cancellationDeadlineHours && <FormHelperText>{formErrors.cancellationDeadlineHours}</FormHelperText>}
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Participants Section */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel>Participants</FormSectionLabel>
          <Box p={2}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Typography variant="body1" color="text.secondary">
                Manage Participants
              </Typography>
              <Button variant="outlined" size="small" onClick={handleAddParticipantClick} startIcon={<AddIcon />}>
                Add Participant
              </Button>
            </Stack>
            {isLoadingParticipants ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              participantUsers?.map((participant) => {
                return (
                  <FormControlLabel
                    key={participant.resourceId}
                    control={<Checkbox checked={selectedParticipants.includes(participant.resourceId)} onChange={() => handleParticipantToggle(participant.resourceId)} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AvatarPlus user={participant} size="mini" />
                      </Box>
                    }
                  />
                )
              })
            )}
            {participantCandidateIds?.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center">
                No participants yet
              </Typography>
            )}
          </Box>
        </FormControl>

        {/* Add Participant Dialog */}
        <Dialog
          open={addParticipantDialogOpen}
          onClose={handleAddParticipantClose}
          maxWidth="sm"
          fullWidth
          disableRestoreFocus
          keepMounted={false}
          sx={{
            '& .MuiDialog-container': {
              alignItems: { xs: 'center', sm: 'center' },
              marginTop: { xs: 0, sm: 'auto' },
            },
          }}
        >
          <DialogTitle>Add Participant</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Search Users"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              margin="normal"
              variant="outlined"
              placeholder="Search by name or email"
              onFocus={(e) => {
                if (window.innerWidth < 600) {
                  window.scrollTo({
                    top: window.innerHeight * 0.2, // Add 20% viewport height offset
                    behavior: 'smooth',
                  })
                }
              }}
            />
            {loadingAvailableUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ mt: 2 }}>
                {filteredUsers.map((user) => (
                  <ListItem key={user.resourceId} disablePadding>
                    <ListItemButton onClick={() => handleParticipantSelect(user.resourceId)}>
                      <ListItemAvatar>
                        <UserAvatar user={user} size="small" />
                      </ListItemAvatar>
                      <ListItemText primary={`${user.firstName} ${user.lastName}`} secondary={user.email} />
                    </ListItemButton>
                  </ListItem>
                ))}
                {filteredUsers.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No users found" secondary="Try a different search term" />
                  </ListItem>
                )}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddParticipantClose} color="inherit">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Session Description */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel required>Session Description</FormSectionLabel>
          <Box p={2}>
            <TextField
              fullWidth
              multiline
              placeholder="Brief description of the session..."
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
              }}
            />
          </Box>
        </FormControl>

        {/* Session Details (Markdown) */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel>Detailed Information</FormSectionLabel>
          <TiptapEditor defaultContent={detailsRef.current} onChange={handleDetailsChange} />
        </FormControl>

        {/* Session Pictures */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <FormSectionLabel>Session Media</FormSectionLabel>
          <Box p={2}>
            <PictureManagement pictures={formData.pictures || []} onPicturesChange={(updatedPictures) => handleChange('pictures', updatedPictures)} editable={true} />
          </Box>
        </FormControl>
      </CardContent>
    </Card>
  )
}
